import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Drawer from '../components/ui/Drawer';
import Spinner from '../components/ui/Spinner';
import { listMockAnswers, listOrgMockSessions } from '../api/mockSessions';

function modeClass(mode) {
  const value = (mode || '').toUpperCase();
  if (value === 'QUESTION_SET') return 'chip-teal';
  if (value === 'RANDOM') return 'chip-navy';
  return 'chip-navy';
}

function recommendationClass(rec) {
  const value = (rec || '').toUpperCase();
  if (value === 'STRONG_YES') return 'chip-teal-solid';
  if (value === 'YES') return 'chip-teal';
  if (value === 'MAYBE') return 'chip-amber';
  if (value === 'NO') return 'chip-rose';
  return 'chip-navy';
}

function scoreClass(score) {
  if (score == null) return 'text-ink-faint';
  if (score >= 70) return 'text-teal-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-rose-600';
}

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function parseAnswerLists(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    return value
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function AnswerItem({ answer, index }) {
  const [open, setOpen] = useState(false);
  const feedback = answer?.aiFeedback || {};
  const strengths = parseAnswerLists(feedback?.strengths);
  const improvements = parseAnswerLists(feedback?.improvements);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">Question {index + 1}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-700">
              {(answer.language || 'unknown').toLowerCase()}
            </span>
            <span className={`text-xs font-semibold ${scoreClass(answer.aiScore)}`}>
              {answer.aiScore == null ? '-' : `${Math.round(answer.aiScore)}/100`}
            </span>
          </div>
        </div>
        <span className="text-slate-400 text-lg leading-none">{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div className="border-t border-slate-200 px-4 py-3 space-y-3 bg-slate-50/60">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">AI Feedback</p>
            <p className="text-sm text-slate-700 mt-1">{feedback?.feedback || '-'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Improvement Tips</p>
            <p className="text-sm text-slate-700 mt-1">{feedback?.improvementTips || '-'}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Time Complexity</p>
              <p className="text-sm text-slate-800 mt-1">{feedback?.timeComplexity || '-'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Space Complexity</p>
              <p className="text-sm text-slate-800 mt-1">{feedback?.spaceComplexity || '-'}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Strengths</p>
              {strengths.length ? (
                <ul className="space-y-1">
                  {strengths.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-slate-400">-</p>}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Improvements</p>
              {improvements.length ? (
                <ul className="space-y-1">
                  {improvements.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-slate-400">-</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MockInterviewsPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [answersBySession, setAnswersBySession] = useState({});
  const [answersLoadingBySession, setAnswersLoadingBySession] = useState({});
  const [answersErrorBySession, setAnswersErrorBySession] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listOrgMockSessions();
        const list = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setSessions(list);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load mock interviews.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) => (s.candidateEmail || '').toLowerCase().includes(q));
  }, [sessions, search]);

  const openDrawer = useCallback(async (session) => {
    setSelected(session);
    if (answersBySession[session.id]) return;
    setAnswersLoadingBySession((prev) => ({ ...prev, [session.id]: true }));
    setAnswersErrorBySession((prev) => ({ ...prev, [session.id]: '' }));
    try {
      const res = await listMockAnswers(session.id);
      const list = Array.isArray(res.data) ? res.data : [];
      setAnswersBySession((prev) => ({ ...prev, [session.id]: list }));
    } catch (err) {
      setAnswersErrorBySession((prev) => ({ ...prev, [session.id]: err.message || 'Failed to load answers.' }));
    } finally {
      setAnswersLoadingBySession((prev) => ({ ...prev, [session.id]: false }));
    }
  }, [answersBySession]);

  return (
    <div className="page-pad page-y max-w-[1400px] mx-auto w-full space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600">AI Interview</p>
          <h1 className="font-heading text-2xl font-bold text-ink tracking-tight">Mock Interviews</h1>
          <p className="text-sm text-ink-faint mt-0.5">{visible.length} sessions across your org</p>
        </div>
        <div className="w-full md:w-[320px]">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by candidate email…"
            className="input"
          />
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16"><Spinner size="md" label="Loading mock interviews..." /></div>
        ) : error ? (
          <div className="py-12 text-center text-sm text-red-600" role="alert">{error}</div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-base font-semibold text-slate-700">No mock interviews yet.</p>
            <p className="text-sm text-slate-500 mt-1">Candidates will appear here after completing an AI interview.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Mode / Qs</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Rec.</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => openDrawer(s)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{s.candidateName || s.candidateEmail}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{s.candidateEmail}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${modeClass(s.mode)}`}>
                        {(s.mode || '-').toLowerCase()}
                      </span>
                      <p className="text-xs text-slate-500 mt-1">{s.questionsCompleted ?? 0}/{s.totalQuestions ?? 0}</p>
                    </td>
                    <td className={`px-4 py-3 font-semibold ${scoreClass(s.overallScore)}`}>
                      {s.overallScore == null ? '-' : `${Math.round(s.overallScore)}/100`}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${recommendationClass(s.hiringRecommendation)}`}>
                        {s.hiringRecommendation || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-600">
                        {s.status || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{formatDate(s.completedAt || s.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        width="lg"
        title={selected ? `Mock Interview — ${selected.candidateEmail}` : 'Mock Interview'}
      >
        {!selected ? null : (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold ${scoreClass(selected.overallScore)} bg-white border border-slate-200`}>
                  Score: {selected.overallScore == null ? '-' : `${Math.round(selected.overallScore)}/100`}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${recommendationClass(selected.hiringRecommendation)}`}>
                  {selected.hiringRecommendation || '-'}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wide ${modeClass(selected.mode)}`}>
                  {(selected.mode || '-').toLowerCase()}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Date</p>
                  <p className="text-slate-800 mt-1">{formatDate(selected.completedAt || selected.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Questions</p>
                  <p className="text-slate-800 mt-1">{selected.questionsCompleted ?? 0}/{selected.totalQuestions ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Status</p>
                  <p className="text-slate-800 mt-1">{selected.status || '-'}</p>
                </div>
              </div>
            </div>

            {answersLoadingBySession[selected.id] ? (
              <div className="py-8"><Spinner size="sm" label="Loading answers..." /></div>
            ) : answersErrorBySession[selected.id] ? (
              <div className="text-sm text-red-600">{answersErrorBySession[selected.id]}</div>
            ) : (answersBySession[selected.id] || []).length === 0 ? (
              <div className="text-sm text-slate-500">No answers recorded.</div>
            ) : (
              <div className="space-y-2">
                {(answersBySession[selected.id] || []).map((answer, index) => (
                  <AnswerItem key={answer.id} answer={answer} index={index} />
                ))}
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
